<template>
  <div id="email_engagement_activity_graph">
    <ContainerInfoItem
      source="email_engagement_activity_graph"
      :title="title"
      :description="range?.label"
      height="fit-content"
    >
      <SkeletonLoader v-if="isLoading" :page="loaderPages.CHART" />

      <LineChart
        v-else-if="chartCategories && chartData"
        id="email_engagement_activity_graph"
        primary-color="#48AF7D"
        :categories="chartCategories"
        :series-data="chartData"
      />

      <EmptyScreen
        v-else
        source="email_engagement_activity_graph"
        text="No email engagement data found!"
        :type="emptyScreenTypes.CHART_AND_TABLE"
      />
    </ContainerInfoItem>
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch, watchEffect} from "vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { formatDateToMonthDay } from "@/src/utils/dateTimeUtils.js";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import LineChart from "@/src/components/charts/LineChart.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const props = defineProps({
  title: { type: String, default: "Email engagement activity" },
});

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);

const chartCategories = ref(null);
const chartData = ref(null);
const isLoading = ref(false);

const seriesColors = {
  "Emails Sent": "#48AF7D",
  "Email Opens": "#2579EF",
  "Email Clicks": "#A146E8",
  "Emails Replied": "#D89435",
  Bounces: "#AC1C1E",
};

const processEmailEngagementData = (data) => {
  if (!data || !data.length) {
    return { categories: null, seriesData: null };
  }
  const sortedData = [...data].sort(
    (a, b) => new Date(a.date) - new Date(b.date),
  );

  const categories = sortedData.map((item) => formatDateToMonthDay(item.date));
  const seriesData = [
    {
      name: "Emails Sent",
      data: sortedData.map((item) => item.email_sent),
      color: seriesColors["Emails Sent"],
    },
    {
      name: "Email Opens",
      data: sortedData.map((item) => item.email_opened),
      color: seriesColors["Email Opens"],
    },
    {
      name: "Email Clicks",
      data: sortedData.map((item) => item.email_clicked),
      color: seriesColors["Email Clicks"],
    },
    {
      name: "Emails Replied",
      data: sortedData.map((item) => item.email_replied),
      color: seriesColors["Emails Replied"],
    },
    {
      name: "Bounces",
      data: sortedData.map((item) => item.email_bounced),
      color: seriesColors["Bounces"],
    },
  ];


  return { categories, seriesData };
};

const fetchEmailEngagementActivity = async () => {
  try {
    isLoading.value = true;

    const params = {
      start_time: range.value?.start,
      end_time: range.value?.end,
      user_id: userIds.value,
      team_id: teamIds.value,
      sequence_id: filters.value?.sequence?.id,
    };

    const response = await AnalyticService.V2.fetchSequenceEmailEngagement(params);

    if (response?.success && response?.data) {
      const data = response?.data;

      const { categories, seriesData } = processEmailEngagementData(data);

      chartCategories.value = categories;
      chartData.value = seriesData;
    } else {
      chartCategories.value = null;
      chartData.value = null;
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching email engagement activity: ",
      error,
    );
    chartCategories.value = null;
    chartData.value = null;
  } finally {
    isLoading.value = false;
  }
};

watch([range, filters, userIds, teamIds], () => {
  if ((range.value && userIds.value && teamIds.value) || (filters.value?.sequence?.id)) {
    fetchEmailEngagementActivity();
  }
}, { deep: true });

onMounted(() => {
  // fetchEmailEngagementActivity();
});

useAnalyticsRefresh(fetchEmailEngagementActivity, [range, userIds, teamIds]);
</script>
